import classes from './modal.module.css';
import cx from 'clsx';
import { modals } from '@mantine/modals';
import { FormEventHandler, PropsWithChildren, ReactNode } from 'react';
import { Button } from '@mantine/core';
import Icon from '@/components/Icon';
import type { RegisteredIcon } from '@/lib/icons';
import type {
  DefaultDesignMeaning,
  ImportanceDesignMeaning,
  SentimentDesignMeaning,
} from '@/types/design-semantics';

type ModalStyleVariants = 'standard' | 'multi-step';

export interface BareModalOptions {
  id?: string;
  title: string;
  withCloseButton?: boolean;
  width?: number | `${number}%`;
  variant?: ModalStyleVariants;
  icon?: RegisteredIcon;
  meaning?:
    | SentimentDesignMeaning
    | ImportanceDesignMeaning
    | DefaultDesignMeaning;
  destructive?: boolean;
  onClose?: () => void;
}

/**
 * Wraps Mantine's `modal.open` with structured defaults and class overrides.
 */
export const openRawModal = ({
  id,
  title,
  width,
  variant,
  withCloseButton = true,
  icon,
  meaning = 'default',
  onClose,
  children,
}: BareModalOptions & PropsWithChildren) =>
  modals.open({
    modalId: id,
    centered: true,
    classNames: {
      content: classes.root,
      header: icon ? classes.headerWithIcon : classes.header,
      body: classes.body,
      close: classes.closeButton,
      title: icon
        ? classes.iconTitle
        : variant === 'multi-step'
          ? classes.multiStepTitle
          : classes.title,
    },
    closeButtonProps: {
      'aria-label': 'Close dialog',
    },
    onClose,
    size: width,
    title: icon ? (
      <>
        <Icon
          className={classes.icon}
          name={icon}
          variant="halo"
          size="lxl"
          meaning={meaning}
          alt=""
        />
        <span>{title}</span>
      </>
    ) : (
      title
    ),
    withCloseButton,
    closeOnClickOutside: withCloseButton,
    closeOnEscape: withCloseButton,
    children,
  });

export interface StandardModalOptions extends BareModalOptions {
  bodySection: ReactNode;
  footerSection?: ReactNode;
}

/**
 * Opens a standard modal with a body and optional footer section.
 */
export const openStandardModal = ({
  bodySection,
  footerSection,
  ...options
}: StandardModalOptions) =>
  openRawModal({
    ...options,
    children: (
      <>
        <div className={classes.content}>{bodySection}</div>
        {footerSection && (
          <footer className={classes.footer}>{footerSection}</footer>
        )}
      </>
    ),
  });

export interface FormModalLabelFooterOptions {
  submitLabel?: string;
  cancelLabel?: string | null;
  footerSection?: never;
}

export interface FormModalNodeFooterOptions extends NonNullable<
  Required<Pick<StandardModalOptions, 'footerSection'>>
> {
  submitLabel?: never;
  cancelLabel?: never;
}

export type FormModalOptions = StandardModalOptions &
  (FormModalLabelFooterOptions | FormModalNodeFooterOptions) & {
    onSubmitHandler: FormEventHandler<HTMLFormElement>;
  };

/**
 * Opens a standard modal with a submittable form inside. Provide either `footerSection`
 * with submission controls, or specify `submitLabel` and `cancelLabel` to use default controls.
 */
export const openFormModal = ({
  id,
  bodySection,
  footerSection,
  onSubmitHandler,
  submitLabel,
  cancelLabel,
  destructive = false,
  meaning = destructive ? 'negative' : undefined,
  ...options
}: FormModalOptions) => {
  const formId = openRawModal({
    id,
    meaning,
    destructive,
    ...options,
    children: (
      <form onSubmit={onSubmitHandler}>
        <div className={classes.content}>{bodySection}</div>
        <footer className={classes.footer}>
          {footerSection ? (
            footerSection
          ) : (
            <>
              {cancelLabel !== null && (
                <Button
                  variant="outline"
                  onClick={() => modals.close(formId)}
                  fullWidth>
                  {cancelLabel ?? 'Cancel'}
                </Button>
              )}
              <Button
                type="submit"
                fullWidth
                className={cx({
                  [classes.negativeButton]: destructive,
                })}>
                {submitLabel ?? 'Submit'}
              </Button>
            </>
          )}
        </footer>
      </form>
    ),
  });
  return formId;
};

export const closeModal = (id: string) => modals.close(id);

export const closeAllModals = () => modals.closeAll();
